/**
 * F-AlgebraExplainer — progressive-disclosure explainer for F-algebras,
 * initial F-algebras, Lambek's lemma, and algebra homomorphisms.
 *
 * The pilot card for the "algebra family". Anchors the framing that an
 * F-algebra is **stronger than the initial state of a state machine**:
 * a structure-preserving rule, not just a starting value.
 *
 * Acceptance criteria (issue #1550):
 *   - F-algebra pilot              ✓ formal-tier square
 *   - Initial F-algebra + cata     ✓ formal-tier `InitialAlgebraDiagram`
 *   - Lambek's lemma               ✓ annotated on the initial-algebra diagram
 *   - Algebra homomorphisms        ✓ formal-tier `AlgebraHomomorphismSquare`
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  AlgebraHomomorphismSquare,
  CalloutBox,
  ExplainerCard,
  FAlgebraDiagram,
  FunctorLiftDiagram,
  InitialAlgebraDiagram,
  WorkedExample,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = THEOREM_COLOR["INV.ESTABLISH"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-falgebra",
  kind: "Categorical structure",
  title: "F-Algebra",
  subtitle: "(A, α : F(A) → A) · μF · cata α · Lambek",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "INV.ESTABLISH (I1)", href: "#explainer-inv-establish", accent: ACCENT },
  { label: "Recursion schemes", href: "#explainer-recursion-schemes" },
  { label: "F-Coalgebra", href: "#explainer-fcoalgebra" },
  { label: "Bialgebra", href: "#explainer-bialgebra" },
  { label: "Adjunction", href: "#explainer-adjunction" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      An <strong>F-algebra</strong> is a small rule that says how to fold one layer of structure
      into a value. Pick the right pattern <MathInline>{tex`F`}</MathInline> and CSK can prove that{" "}
      <em>any</em> safe builder is exactly an F-algebra — and that unsafe ones cannot even be
      written down.
    </p>
  ),
  intuition: (
    <>
      <p>
        Imagine you are summing a tree of numbers. A tree node is either a <em>leaf</em> carrying a
        number, or a <em>branch</em> joining two sub-totals. That shape is the functor{" "}
        <MathInline>{tex`F(X) = \mathbb{N} + (X \times X)`}</MathInline>: "one layer is either a
        leaf or a join of two things-of-type-<MathInline>{tex`X`}</MathInline>." An{" "}
        <strong>F-algebra</strong> on a carrier <MathInline>{tex`A`}</MathInline> is one function
        that handles both cases at once and gives you back a single{" "}
        <MathInline>{tex`A`}</MathInline> — for summing, that function is just{" "}
        <MathInline>{tex`\mathrm{leaf}\ n \mapsto n`}</MathInline> together with{" "}
        <MathInline>{tex`\mathrm{branch}\ (l, r) \mapsto l + r`}</MathInline>.
      </p>
      <p>
        The strength comes from a quiet rule: <em>the same algebra must work for every shape</em>.
        There is no "first move" special case, no hidden seed, no out-of-band trapdoor. The
        canonical (<strong>initial</strong>) F-algebra <MathInline>{tex`\mu F`}</MathInline> is the
        smallest type you can build out of <MathInline>{tex`F`}</MathInline>-shapes — and every
        other algebra is reachable from it by exactly one fold, the <strong>catamorphism</strong>.
      </p>
      <CalloutBox eyebrow="The one-line takeaway" accent={ACCENT}>
        <em>F-algebras turn a "starting value" into a structural contract.</em> A state machine's{" "}
        <MathInline>{tex`s_{0}`}</MathInline> is a value; an F-algebra is a structure-preserving
        rule, and that promotion is what lets CSK enforce safety <strong>by construction</strong>.
      </CalloutBox>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`F : C \to C`}</MathInline> be an endofunctor on a category{" "}
        <MathInline>{tex`C`}</MathInline>. An <strong>F-algebra</strong> is a pair{" "}
        <MathInline>{tex`(A, \alpha)`}</MathInline> with{" "}
        <MathInline>{tex`\alpha : F(A) \to A`}</MathInline>. F-algebras form a category{" "}
        <MathInline>{tex`F\text{-}\mathrm{Alg}(F)`}</MathInline> whose morphisms are commuting
        squares:
      </p>
      <AlgebraHomomorphismSquare accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>
        A morphism <MathInline>{tex`h : (A, \alpha) \to (B, \beta)`}</MathInline> satisfies{" "}
        <MathInline>{tex`h \circ \alpha = \beta \circ F(h)`}</MathInline> — it preserves the algebra
        structure, not just the underlying values. Refactoring along an F-algebra homomorphism is
        exactly "swap implementations without disturbing the contract."
      </p>
      <p style={{ marginTop: "0.75rem" }}>
        The key advantage over a state machine is <strong>functoriality</strong>. Because{" "}
        <MathInline>{tex`F`}</MathInline> is a functor, any morphism{" "}
        <MathInline>{tex`f : A \to B`}</MathInline> in the base category lifts automatically to{" "}
        <MathInline>{tex`F(f) : F(A) \to F(B)`}</MathInline>:
      </p>
      <FunctorLiftDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.5rem", fontSize: "0.875rem" }}>
        A state machine transition <MathInline>{tex`s_{0} \mapsto s_{1}`}</MathInline> is a{" "}
        <em>point</em>, not a functor. It has no mechanism to transport a function{" "}
        <MathInline>{tex`f : A \to B`}</MathInline> along transitions — there is no{" "}
        <MathInline>{tex`F(f)`}</MathInline> to appeal to. This is why a state machine cannot
        guarantee that a refactoring preserves every transition contract.
      </p>
      <p style={{ marginTop: "0.75rem" }}>
        When the <strong>initial F-algebra</strong> <MathInline>{tex`\mu F`}</MathInline> exists, it
        pins down two guarantees no state machine can offer:
      </p>
      <InitialAlgebraDiagram accent={ACCENT} />
      <ul style={{ marginTop: "0.5rem" }}>
        <li>
          <strong>Universal property (catamorphism).</strong> For any F-algebra{" "}
          <MathInline>{tex`(A, \alpha)`}</MathInline> there is exactly one F-algebra morphism{" "}
          <MathInline>{tex`\mathrm{cata}\ \alpha : \mu F \to A`}</MathInline>. Two folds that agree
          on the shape <MathInline>{tex`F`}</MathInline> are the same fold — no ambiguity, no hidden
          bias.
        </li>
        <li>
          <strong>Lambek's lemma.</strong> The structure map{" "}
          <MathInline>{tex`\mathrm{in} : F(\mu F) \to \mu F`}</MathInline> is an isomorphism. The
          initial carrier <em>is</em> its own unfolding; there is no extra hidden state to leak.
        </li>
      </ul>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>F-algebras are stronger than the initial state of a state machine.</strong> CSK
        leans on four properties that a bare <MathInline>{tex`s_{0}`}</MathInline> simply cannot
        express:
      </p>
      <ul>
        <li>
          <strong>Functoriality.</strong> <MathInline>{tex`F`}</MathInline> transports morphisms: if{" "}
          <MathInline>{tex`f : A \to B`}</MathInline>, then{" "}
          <MathInline>{tex`F(f) : F(A) \to F(B)`}</MathInline> automatically. State-machine
          transitions have no such lift, so refactoring a state machine can break the transition
          graph silently.
        </li>
        <li>
          <strong>Structure-preserving morphisms.</strong> The square{" "}
          <MathInline>{tex`h \circ \alpha = \beta \circ F(h)`}</MathInline> forces{" "}
          <MathInline>{tex`h`}</MathInline> to preserve <em>the transition discipline</em>, not just
          the carrier. A refactor along a homomorphism cannot break an invariant.
        </li>
        <li>
          <strong>Universal property.</strong> The catamorphism out of{" "}
          <MathInline>{tex`\mu F`}</MathInline> is unique. Two state-machine implementations may
          produce the same trace yet hide different invariants;{" "}
          <MathInline>{tex`\mathrm{cata}\ \beta`}</MathInline> is determined by{" "}
          <MathInline>{tex`\beta`}</MathInline> alone.
        </li>
        <li>
          <strong>Recursion schemes.</strong> <code>cata</code>, <code>para</code>,{" "}
          <code>zygo</code> and friends are derived from initial algebras — provably correct folds,
          never ad hoc traversals.
        </li>
      </ul>
      <WorkedExample
        eyebrow="Real-world story"
        title="A session-audit verdict that cannot be forged"
        accent={ACCENT}
        scenario={
          <>
            <p style={{ margin: "0 0 0.5rem" }}>
              A login service writes an append-only log. The shape of one log entry is{" "}
              <MathInline>{tex`F(X) = 1 + (\mathrm{LoginEvent} \times X)`}</MathInline>: either "no
              more events" or "one event and the rest of the log." The initial algebra is{" "}
              <MathInline>{tex`\mu F`}</MathInline>, which here is exactly the type of session
              traces. An <strong>auditor</strong> is just an F-algebra{" "}
              <MathInline>{tex`(\mathrm{Verdict}, \alpha)`}</MathInline> that says how to combine
              one event with the verdict of the tail;{" "}
              <MathInline>{tex`\mathrm{cata}\ \alpha`}</MathInline> is the unique auditor.
            </p>
          </>
        }
        withoutStructure={
          <>
            With a state-machine seed, an attacker can ship a verdict computed by a separate
            stateful object that reads partial events and emits a verdict early — same{" "}
            <MathInline>{tex`s_{0}`}</MathInline>, different verdict on the same trace. The
            auditor's behavior is not determined by its rule.
          </>
        }
        withStructure={
          <>
            Because{" "}
            <MathInline>{tex`\mathrm{cata}\ \alpha : \mu F \to \mathrm{Verdict}`}</MathInline> is
            unique, two implementations that agree on the rule{" "}
            <MathInline>{tex`\alpha`}</MathInline> agree on every trace by construction. There is no
            other morphism out of <MathInline>{tex`\mu F`}</MathInline> for the attacker to be — the
            bypassed auditor is <em>structurally inexpressible</em>.
          </>
        }
      />
      <p style={{ marginTop: "0.75rem" }}>
        This is exactly why <strong>INV.ESTABLISH</strong> models invariant establishment as an
        F-algebra rather than as a state-machine seed: every legal builder is an algebra, every
        refactoring is a homomorphism, and <code>μF</code> is "the smallest type whose validity is
        closed under construction." The unsafe builder is not <em>filtered out at runtime</em>; it
        is <em>structurally inexpressible</em>.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras (canonical reference)",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "§2 F-Algebras, §6 Free monads / free algebras, §15 worked AI-safety example.",
    },
    {
      label: "Lambek's lemma (Agda)",
      path: "agda/Foundation/BeckChevalley/CategoriesLib/LambekInvariant.agda",
      note: "Mechanized statement that α on the initial F-algebra is an isomorphism.",
    },
    {
      label: "Recursion-Scheme-Analysis (theory)",
      path: "theory/algebra/Recursion-Scheme-Analysis-Of-Constructive-Tactics.md",
      note: "Catamorphism, paramorphism, zygomorphism: provably correct folds.",
    },
    {
      label: "Recursion schemes (Agda)",
      path: "agda/MathLibCore/Lemmas/RecursionSchemes/Algebras.agda",
      note: "Mechanized algebra-side scheme combinators (cata, para, zygo).",
    },
    {
      label: "Structural Invariant Safety Guarantees manuscript (PR #1544)",
      path: "https://github.com/InvariantHoldings/categorical-safety-kernel/pull/1544",
      note: "Chapter 02 (F-Algebras / Invariant Establishment) and Chapter 04 (joint DPG).",
    },
    {
      label: "Theorem Canon",
      path: "docs/reference/Theorem-Canon.md",
      note: "Source of truth for the SSM table.",
    },
  ],
};

export function FAlgebraExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<FAlgebraDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
